import React, { useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import { REGISTER_EXPERT_MUTATION, UNREGISTER_EXPERT_MUTATION } from 'Queries/Mutations';
import { PUBLIC_GET_CIAM_STATUS_BY_EMAIL } from 'Queries/Queries';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { PUBLIC_NO_AUTH_CONFIG } from 'components/ApolloComponent/Apollo';
import { CustomModal } from 'components/CustomModal';
import { InputField } from 'components/FormHelper/InputField';

import { EIV_STATUS_SALES_FORCE, POPUP_TYPE } from 'global/constants';

import { CIAM_EMAIL_ERROR_TYPES, checkCiamEmailByQuery } from 'utils/ciamEmailValidation.utils';

const isDuplicateEmailError = (error) => {
  const statuses = [
    error?.networkError?.statusCode,
    ...(error?.graphQLErrors || []).map((graphqlError) => graphqlError?.extensions?.status),
  ];
  const messages = [
    error?.message,
    error?.networkError?.message,
    ...(error?.graphQLErrors || []).flatMap((graphqlError) => [
      graphqlError?.message,
      graphqlError?.extensions?.code,
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    statuses.some((status) => [400, 409, '400', '409'].includes(status)) ||
    /already\s+(exists|used|registered)|duplicate[_\s-]*email|bereits\s+(vergeben|in\s+verwendung|verwendet|registriert)/.test(
      messages
    )
  );
};

export const SubAccountsPopup = ({
  isOpen,
  selectedExpert,
  onClose,
  onSetInProgress,
  forcePopupType,
  experts,
}) => {
  const [popupType, setPopupType] = useState(null);
  const [unregisterExpertMutation] = useMutation(UNREGISTER_EXPERT_MUTATION);
  const [registerExpertMutation] = useMutation(REGISTER_EXPERT_MUTATION);
  const [getCiamStatusByEmail] = useLazyQuery(
    PUBLIC_GET_CIAM_STATUS_BY_EMAIL,
    PUBLIC_NO_AUTH_CONFIG
  );
  const [isLoading, setIsLoading] = useState(false);
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        netzportalEmail: Yup.string()
          .email('Ungültige E-Mail-Adresse')
          .required('E-Mail-Adresse ist erforderlich')
          .nullable()
          .test('unique-email', 'E-Mail-Adresse wird bereits verwendet', (value) => {
            if (!selectedExpert?.id) return true;
            const normalized = (value || '').trim().toLowerCase();
            if (!normalized) return true;
            const ownEmail = (selectedExpert?.netzportalEmail || '').trim().toLowerCase();
            if (normalized === ownEmail) return true;
            const exists = (experts || []).some(
              (expert) =>
                expert?.id !== selectedExpert?.id &&
                (expert?.netzportalEmail || '').trim().toLowerCase() === normalized
            );
            return !exists;
          }),
      }),
    [experts, selectedExpert?.id]
  );
  const formik = useFormik({
    initialValues: {
      netzportalEmail: selectedExpert?.netzportalEmail || '',
      salesForceStatus: selectedExpert?.salesForceStatus || '',
    },
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
  });

  useEffect(() => {
    if (!selectedExpert) return;
    if (forcePopupType) {
      setPopupType(forcePopupType);
      return;
    }
    formik.setFieldValue('netzportalEmail', selectedExpert.netzportalEmail).then(() => {
      formik.validateForm();
    });
    formik.setFieldValue('salesForceStatus', selectedExpert.salesForceStatus);
    if (
      [EIV_STATUS_SALES_FORCE.ACTIVE, EIV_STATUS_SALES_FORCE.IN_PROGRESS].includes(
        selectedExpert.salesForceStatus
      )
    ) {
      setPopupType(POPUP_TYPE.DELETE);
    } else {
      setPopupType(POPUP_TYPE.CREATE);
    }
  }, [selectedExpert, isOpen, forcePopupType]);

  const closeAndResetForm = () => {
    onClose();
    formik.resetForm();
  };
  const unregisterExpert = () => {
    setIsLoading(true);
    unregisterExpertMutation({ variables: { expertId: selectedExpert.id } })
      .then(() => {
        selectedExpert.salesForceStatus = EIV_STATUS_SALES_FORCE.NOT_SET;
        closeAndResetForm();
      })
      .catch(() => {
        setPopupType(POPUP_TYPE.ERROR);
        console.error('Error unregistering expert');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const registerExpert = async () => {
    setIsLoading(true);
    const ciamResult = await checkCiamEmailByQuery(
      getCiamStatusByEmail,
      formik.values.netzportalEmail
    );
    if (!ciamResult.isValid) {
      setPopupType(
        [
          CIAM_EMAIL_ERROR_TYPES.ACTIVE,
          CIAM_EMAIL_ERROR_TYPES.PENDING,
          CIAM_EMAIL_ERROR_TYPES.DUPLICATE,
        ].includes(ciamResult.errorType)
          ? CIAM_EMAIL_ERROR_TYPES.DUPLICATE
          : POPUP_TYPE.ERROR
      );
      setIsLoading(false);
      return;
    }

    registerExpertMutation({
      variables: { expertId: selectedExpert.id, email: formik.values.netzportalEmail },
    })
      .then((response) => {
        const status = response.data?.registerFachkraft?.status;
        if (status === 200 || status === '200') {
          selectedExpert.salesForceStatus = EIV_STATUS_SALES_FORCE.IN_PROGRESS;
          setPopupType(POPUP_TYPE.CREATED);
          if (onSetInProgress) {
            onSetInProgress(formik);
          }
        } else if ([400, 409, '400', '409'].includes(status)) {
          setPopupType(CIAM_EMAIL_ERROR_TYPES.DUPLICATE);
        } else {
          setPopupType(POPUP_TYPE.ERROR);
          console.error('Error registering expert');
        }
      })
      .catch((error) => {
        setPopupType(
          isDuplicateEmailError(error) ? CIAM_EMAIL_ERROR_TYPES.DUPLICATE : POPUP_TYPE.ERROR
        );
        console.error('Error registering expert');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const submitPopup = () => {
    if ([POPUP_TYPE.DELETE].includes(popupType)) {
      unregisterExpert();
      return;
    }
    if (popupType === POPUP_TYPE.CREATE) {
      registerExpert();
      return;
    }
    if (popupType === POPUP_TYPE.CREATED) {
      closeAndResetForm();
      return;
    }
    if ([POPUP_TYPE.ERROR, CIAM_EMAIL_ERROR_TYPES.DUPLICATE].includes(popupType)) {
      closeAndResetForm();
      return;
    }
    setPopupType(POPUP_TYPE.ERROR);
    console.error('Error submitting popup');
  };

  const popupTitle = () => {
    if (popupType === POPUP_TYPE.DELETE) return 'Zusatzkonto deaktivieren';
    if (popupType === POPUP_TYPE.CREATE) return 'Zusatzkonto vergeben';
    if (popupType === POPUP_TYPE.ERROR || popupType === CIAM_EMAIL_ERROR_TYPES.DUPLICATE) {
      return 'Fehler beim Vergeben des Zusatzkontos';
    }
    return 'Zusatzkonto erfolgreich vergeben';
  };

  const popupText = () => {
    if (popupType === POPUP_TYPE.DELETE) {
      return `Sind Sie sicher, dass Sie das Zusatzkonto für die Fachkraft ${selectedExpert?.completeName} deaktivieren wollen?`;
    }
    if (popupType === POPUP_TYPE.CREATE) {
      return `Bitte ergänzen Sie die E-Mail-Adresse für die Fachkraft ${selectedExpert?.completeName}.`;
    }
    if (popupType === CIAM_EMAIL_ERROR_TYPES.DUPLICATE) {
      return 'Fehler beim Vergeben des Zusatzkontos. Die angegebene E-Mail-Adresse ist bereits in Verwendung.';
    }
    if (popupType === POPUP_TYPE.ERROR) {
      return `Fehler beim Vergeben des Zusatzkontos für die Fachkraft ${selectedExpert?.completeName}.`;
    }
    return (
      <div>
        <div>
          Ein Zusatzkonto für die Fachkraft{' '}
          <span className="sub-account-popup-expert-name">{selectedExpert?.completeName}</span>{' '}
          wurde erfolgreich vergeben.
        </div>
        <br />
        <div>
          Die neu eingetragene E-Mail-Adresse erhält eine Registrierung-E-Mail zur Passwortvergabe.{' '}
          <br />
          Danach können Sie sich im mein.Auftragsportal sowie im e-fix einloggen.
        </div>
      </div>
    );
  };

  const modalContent = () => (
    <div className="sub-account-popup-container">
      <div>{popupText()}</div>
      {popupType === POPUP_TYPE.CREATE && (
        <div className="sub-account-popup-input-container">
          <InputField
            label="E-Mail-Adresse"
            disabled={Boolean(selectedExpert?.netzportalEmail) || isLoading}
            name="netzportalEmail"
            fullWidth
            value={formik.values.netzportalEmail}
            onChange={(e) => {
              formik.setFieldValue('netzportalEmail', e.target.value.trim().toLowerCase());
            }}
            error={Boolean(formik.errors.netzportalEmail)}
            helperText={formik.errors.netzportalEmail}
          />
        </div>
      )}
    </div>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      title={popupTitle()}
      closeCancelEventModal={closeAndResetForm}
      ctaButtons={
        <>
          <ActionButton
            loading={isLoading}
            disabled={popupType === POPUP_TYPE.CREATE && !formik.isValid}
            onClick={submitPopup}
            title="Ok"
          />
          {![POPUP_TYPE.ERROR, CIAM_EMAIL_ERROR_TYPES.DUPLICATE, POPUP_TYPE.CREATED].includes(
            popupType
          ) && <ActionButton inverted onClick={closeAndResetForm} title="Abbrechen" />}
        </>
      }
      content={modalContent()}
    />
  );
};

SubAccountsPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedExpert: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSetInProgress: PropTypes.func,
  forcePopupType: PropTypes.string,
  experts: PropTypes.array,
};
SubAccountsPopup.defaultProps = {
  selectedExpert: null,
  onSetInProgress: undefined,
  forcePopupType: undefined,
  experts: [],
};
